import React, { useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { Box, GlobalStyles } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useCreateBlockNote } from '@blocknote/react';
import { BlockNoteView } from '@blocknote/mantine';
// El resolver de eslint no entiende los subpath-exports de estos paquetes; webpack sí.
// eslint-disable-next-line import/no-unresolved
import * as locales from '@blocknote/core/locales';
import contentApi from 'api/graphql/content';
// eslint-disable-next-line import/no-unresolved
import '@blocknote/mantine/style.css';

// Theme BlockNote alineado al design system OTRIX (off-black/claro + acento dorado).
// Se deriva de la paleta MUI activa para que el editor cambie con el modo (dark/light).
const buildOtrixTheme = (palette) => ({
  colors: {
    editor: { text: palette.text.primary, background: palette.surface.content },
    menu: { text: palette.text.primary, background: palette.surface.raised },
    tooltip: { text: palette.text.primary, background: palette.surface.sidebar },
    hovered: { text: palette.text.primary, background: palette.surface.hover },
    selected: { text: palette.ink.main, background: palette.gold.main },
    disabled: { text: palette.text.disabled, background: palette.surface.raised },
    shadow: palette.mode === 'dark' ? 'rgba(0, 0, 0, 0.45)' : 'rgba(17, 21, 27, 0.16)',
    border: palette.hairline.strong,
    sideMenu: palette.text.secondary,
  },
  borderRadius: 10,
  fontFamily: "'Plus Jakarta Sans', sans-serif",
});

// Popups flotantes de BlockNote (menú "/", toolbar, menú de bloque):
// 1) altura máxima + scroll interno → el menú CABE debajo del cursor y floating-ui
//    deja de voltearlo hacia arriba (donde se cortaba contra el borde superior);
// 2) z-index por encima del AppBar de MUI (~1100): BlockNote usa 10-50 por defecto.
// Todos los popups flotantes de BlockNote/Mantine deben quedar POR ENCIMA del MUI Dialog
// (z-index 1300) cuando el editor vive dentro de un modal (recetas/programas). Se cubren tanto los
// menús in-place como los porteados a body (mantine Popover/Menu) con un z-index altísimo.
const FLOATING_SELECTORS = [
  '.bn-suggestion-menu',
  '.bn-formatting-toolbar',
  '.bn-link-toolbar',
  '.bn-color-picker-dropdown',
  '.bn-table-handle-menu',
  '.bn-side-menu',
  '.mantine-Menu-dropdown',
  '.mantine-Popover-dropdown',
  '.bn-container > div:not([class])',
].join(', ');

const blockNoteGlobalStyles = (
  <GlobalStyles
    styles={{
      '.bn-suggestion-menu': {
        maxHeight: 'min(340px, 45vh)',
        overflowY: 'auto',
        // Ancho acotado al viewport: en móvil el menú del "/" se salía por la derecha.
        maxWidth: 'min(320px, calc(100vw - 32px))',
        boxSizing: 'border-box',
      },
      [FLOATING_SELECTORS]: {
        zIndex: '2147483000 !important',
      },
      // Cada opción del menú "/" lleva título y subtítulo ("Encabezado 1" + "Encabezado de
      // sección"), y ambos viven en un contenedor con altura fija. Con el interlineado del
      // documento, el título ocupaba dos renglones y se salía de su caja, montándose sobre la
      // opción siguiente: el menú entero se leía como un amasijo de texto. Se le devuelve alto
      // automático a la fila y a sus dos textos.
      //
      // Las clases llevan el prefijo `bn-mt-` (mantine), no `bn-`: apuntar a las que no existen
      // fue justo lo que dejó el problema a medias la primera vez.
      '.bn-suggestion-menu-item, .mantine-Menu-item': {
        alignItems: 'flex-start !important',
        boxSizing: 'border-box',
        height: 'auto !important',
        minHeight: 'unset !important',
        paddingBlock: '8px !important',
      },
      '.bn-mt-suggestion-menu-item-body': {
        display: 'flex !important',
        flexDirection: 'column !important',
        gap: '2px',
        minWidth: 0,
        overflow: 'hidden',
      },
      '.bn-mt-suggestion-menu-item-title': {
        display: 'block !important',
        fontWeight: 600,
        lineHeight: '1.3 !important',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
      },
      '.bn-mt-suggestion-menu-item-subtitle': {
        display: 'block !important',
        fontSize: '0.75rem',
        lineHeight: '1.3 !important',
        opacity: 0.7,
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
      },
      // El atajo de teclado no debe encogerse ni empujar al texto.
      '.bn-mt-suggestion-menu-item-section': { flexShrink: 0 },
      // Panel de "Subir / Incrustar" de los bloques de archivo, imagen y video. Se dibuja con el
      // ancho de su contenido, así que en móvil desbordaba la pantalla y las pestañas quedaban
      // cortadas por ambos lados.
      '.bn-panel, .bn-file-panel, [class*="bn-panel"]': {
        boxSizing: 'border-box',
        maxWidth: 'min(340px, calc(100vw - 32px))',
        width: '100%',
      },
      '.bn-panel input, .bn-file-panel input': {
        boxSizing: 'border-box',
        maxWidth: '100%',
      },
      // Menú lateral del bloque (el "+" y los seis puntos). En táctil, mantener pulsado abría
      // ADEMÁS el menú del sistema operativo ("copiar", "buscar", selección azul), que tapaba el
      // menú del propio editor. Esto lo silencia.
      //
      // Nota: reordenar ARRASTRANDO no funciona en móvil y no es algo que se arregle desde aquí.
      // BlockNote implementa el arrastre con la API HTML5 (`dragstart`), y los navegadores
      // móviles no emiten ese evento desde un toque. Los seis puntos siguen abriendo el menú del
      // bloque, que es lo que sí responde al tacto.
      '.bn-side-menu, .bn-side-menu *': {
        WebkitTouchCallout: 'none',
        WebkitUserSelect: 'none',
        userSelect: 'none',
      },
    }}
  />
);

// HTML "vacío" de BlockNote → '' para no marcar el form como sucio sin contenido real.
const normalizeHtml = (html) => {
  const text = (html || '').replace(/<[^>]+>/g, '').trim();
  const hasMedia = /<(img|video|audio|iframe|table)/i.test(html || '');
  return text === '' && !hasMedia ? '' : html;
};

const ALIGNABLE_MEDIA_BLOCKS = new Set(['image', 'video']);

// Recoge la alineación (textAlignment) de cada bloque de media indexada por su URL.
const collectMediaAlignment = (blocks, map = {}) => {
  blocks.forEach((block) => {
    const { url, textAlignment } = block.props || {};
    if (ALIGNABLE_MEDIA_BLOCKS.has(block.type) && url && textAlignment) {
      map[url] = textAlignment;
    }
    if (block.children?.length) collectMediaAlignment(block.children, map);
  });
  return map;
};

// BlockNote PIERDE la alineación de media al exportar HTML "lossy" (su `toExternalHTML`
// para imágenes/videos emite el elemento sin el wrapper que lleva `data-text-alignment`,
// a diferencia del texto). Aquí la re-estampamos sobre el propio elemento (o su <figure>
// cuando hay caption) como `data-text-alignment`: ese atributo lo honra proseStyles en el
// render Y lo re-parsea BlockNote al reabrir para editar (round-trip intacto).
const applyMediaAlignment = (html, alignByUrl) => {
  if (!html || Object.keys(alignByUrl).length === 0) return html;
  const doc = new DOMParser().parseFromString(html, 'text/html');
  doc.querySelectorAll('img, video').forEach((el) => {
    const align = alignByUrl[el.getAttribute('src')];
    if (!align || align === 'left') return;
    (el.closest('figure') || el).setAttribute('data-text-alignment', align);
  });
  return doc.body.innerHTML;
};

/**
 * Editor de contenido premium para Noticias y T&C, basado en BlockNote
 * (bloques estilo Notion: menú "/", arrastrar bloques, toolbar flotante,
 * imágenes con resize). Habla HTML estándar con el resto del sistema:
 * - carga el HTML existente con tryParseHTMLToBlocks (compatible con lo publicado)
 * - emite HTML con blocksToHTMLLossy (lo consume ContentRenderer + DOMPurify)
 * Las imágenes se suben por el mismo flujo GraphQL (uploadContentImage).
 */
function BlockNoteContentEditor({
  value = '',
  onChange,
  minHeight = 380,
  lossless = false,
  uploadFn = null
}) {
  const muiTheme = useTheme();
  const otrixTheme = buildOtrixTheme(muiTheme.palette);
  const { i18n } = useTranslation();
  const lastEmittedRef = useRef(null);

  // El editor se crea una sola vez; guardamos la función de subida en un ref para que el
  // closure de `uploadFile` siempre use la última (Noticias/T&C usan uploadMedia; el foro Play
  // usa uploadPlayMedia, autorizado para clientes).
  const uploadFnRef = useRef(uploadFn);
  uploadFnRef.current = uploadFn || contentApi.uploadMedia;

  const editor = useCreateBlockNote({
    dictionary: i18n.language === 'es' ? locales.es : locales.en,
    // Tablas avanzadas: combinar/dividir celdas (como Excel), encabezados y color de celda.
    // El editor exporta lossless (blocksToFullHTML) en programas/recetas → la tabla con celdas
    // combinadas hace round-trip íntegra (antes el export "lossy" la corrompía y eso disparaba
    // el crash de TableHandlesController al reabrir/manipular).
    tables: {
      splitCells: true,
      cellBackgroundColor: true,
      cellTextColor: true,
      headers: true,
    },
    uploadFile: async (file) => {
      // Imagen, video o audio: una sola mutation que guarda en S3 (máx. 60 MB).
      const { data } = await uploadFnRef.current(file);
      // La URL viene bajo la clave de la mutation usada (uploadContentMedia / uploadPlayMedia).
      const url = data?.uploadContentMedia?.url || data?.uploadPlayMedia?.url;
      if (!url) throw new Error('upload failed');
      return url;
    },
  });

  // Sincroniza contenido EXTERNO (cargar noticia/T&C en edición) sin pisar lo
  // que el usuario está escribiendo (solo si difiere de lo último emitido).
  useEffect(() => {
    if (!editor) return;
    const incoming = value || '';
    if (incoming === lastEmittedRef.current) return;

    (async () => {
      const blocks = await editor.tryParseHTMLToBlocks(incoming);
      editor.replaceBlocks(editor.document, blocks);
      lastEmittedRef.current = incoming;
    })();
  }, [value, editor]);

  const handleChange = async () => {
    // `lossless` (programas/recetas) usa blocksToFullHTML → conserva TODOS los bloques de BlockNote
    // (lista plegable/toggle, checklists, etc.) al guardar y al re-editar. El export "lossy" los
    // descartaba. Noticias/T&C siguen en lossy (compatibilidad con su contenido existente).
    const exported = lossless
      ? await editor.blocksToFullHTML(editor.document)
      : await editor.blocksToHTMLLossy(editor.document);
    const aligned = applyMediaAlignment(exported, collectMediaAlignment(editor.document));
    const html = normalizeHtml(aligned);
    lastEmittedRef.current = html;
    onChange(html);
  };

  return (
    <Box
      sx={{
        borderRadius: '14px',
        border: '1px solid',
        borderColor: 'hairline.main',
        backgroundColor: 'surface.content',
        position: 'relative',
        // Lienzo tipo artículo: columna de escritura centrada y aire vertical.
        '& .bn-editor': {
          minHeight,
          maxWidth: 820,
          mx: 'auto',
          py: { xs: 2.5, sm: 4 },
          backgroundColor: 'transparent',
        },
      }}>
      {blockNoteGlobalStyles}
      <BlockNoteView editor={editor} theme={otrixTheme} onChange={handleChange} />
    </Box>
  );
}

BlockNoteContentEditor.propTypes = {
  value: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  minHeight: PropTypes.number,
  lossless: PropTypes.bool,
  // Subidor de multimedia embebida. Por defecto Noticias/T&C (uploadMedia, admin);
  // el foro Play pasa uploadPlayMedia (autorizado para clientes).
  uploadFn: PropTypes.func,
};

export default BlockNoteContentEditor;
